import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collateralApi, ApiError } from '../services/collateralApi';

export type FilterKey = 'guaranteeStatus' | 'intensity' | 'operationType' | 'obligationType';

export default function useVerificationConfigPageList() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const [showNewForm, setShowNewForm] = useState(false);
    const [filters, setFilters] = useState<Record<FilterKey, string>>({
        guaranteeStatus: '', intensity: '', operationType: '', obligationType: '',
    });
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('operationCode');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
    const [rows, setRows] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [apiInfo, setApiInfo] = useState({ version: '', environment: '' });
    const [filterCounts, setFilterCounts] = useState<Record<string, Record<string, number>>>({});
    const pageSize = 50;

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const result = await collateralApi.getVerificationConfigs({
                page, pageSize,
                search: search || undefined,
                guaranteeStatus: filters.guaranteeStatus || undefined,
                intensity: filters.intensity || undefined,
                operationType: filters.operationType || undefined,
                obligationType: filters.obligationType || undefined,
                sortField: sortField || undefined,
                sortOrder: sortOrder || undefined,
            });
            setRows(result.items || []);
            setTotal(result.total || 0);
            setApiInfo({ version: result.apiVersion || '', environment: result.environment || '' });
            setFilterCounts(result.filterCounts || {});
        } catch (e: any) {
            if (e instanceof ApiError) {
                if (e.status === 403) return navigate('/denied');
                if (e.status === 404) return navigate('/not-found');
            }
            navigate('/error');
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, filters, search, sortField, sortOrder, navigate]);

    useEffect(() => { fetchData(); }, [fetchData, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const setFilter = (key: FilterKey, value: string) => {
        setFilters(prev => ({ ...prev, [key]: prev[key] === value ? '' : value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ guaranteeStatus: '', intensity: '', operationType: '', obligationType: '' });
        setPage(1);
    };

    const hasActiveFilter = Object.values(filters).some(v => v !== '');

    const handleRowClick = (row: any) => {
        navigate(`/collateral/verification-config/${row.id}/view`);
    };

    return {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, filterCounts, filters,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        hasActiveFilter, refresh,
        setFilter, clearFilters, handleRowClick,
    };
}
